import { useState, useEffect, useCallback, memo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";

import debounce from "lodash/debounce";

import PowerSearchField from "./power-search-field";
import { searchTopics, resetSearchTopics, setPowerSearchUserInput, resetPowerSearchUserInput } from "../../actions";
import { CONSTANTS } from '../../constants/app-constant';

import "./categories.scss";

const Categories = memo(function Categories(props) {
  const {
    onDisciplineCatSelect = () => { },
    onOrgSysCatSelect = () => { },
    onExamTypeCatSelect = () => { },
    onTopicsCatSelect = () => { },
    onSubTopicsCatSelect = () => { },
    selectedDiscipline = [],
    selectedOrgSystem = [],
    selectedExamType = [],
    selectedTopics = [],
    selectedSubTopics = [],
    totalDiscipline = [],
    totalOrgSystem = [],
    examType = [],
    isViewDisabled = false,
    hideCategoryWhenEmpty = false,
    onPowerSearchModeChange = () => { },
    hidePowerSearch = false,
    // The accordion open on first render. Only the exam picker sets it (for a
    // QLink signup, so the preselected exam is in view).
    defaultExpanded = "discipline"
  } = props;

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [toggleSearchEnabled, setToggleSearchEnabled] = useState(false);
  const [toggleSearchSwitch, setToggleSearchSwitch] = useState(false);
  const [isSuggestionSelected, setIsSuggestionSelected] = useState(false);
  const dispatch = useDispatch();

  const categoryMap = {
    discipline: { selected: selectedDiscipline, handler: onDisciplineCatSelect, total: totalDiscipline },
    organSystem: { selected: selectedOrgSystem, handler: onOrgSysCatSelect, total: totalOrgSystem },
    examType: { selected: selectedExamType, handler: onExamTypeCatSelect, total: examType },
    topics: { selected: selectedTopics, handler: onTopicsCatSelect },
    subtopics: { selected: selectedSubTopics, handler: onSubTopicsCatSelect }
  };

  const onSearchResults = useSelector((state) => state.onTopicsSearch);
  const isSearchLoading = useSelector((state) => state.onTopicsSearch?.loading);
  const searchError = useSelector((state) => state.onTopicsSearch?.error);
  const powerSearchUserInput = useSelector((state) => state.powerSearchUserInput);
  const userPreferenceData = useSelector((state) => state.getUserPreference?.preference);

  useEffect(() => {
    if (toggleSearchEnabled && toggleSearchSwitch) {
      if (powerSearchUserInput?.searchQuery !== undefined) {
        setSearchQuery(powerSearchUserInput.searchQuery);
        if (powerSearchUserInput?.searchQuery?.trim().length >= CONSTANTS.powerSearchCharLength) {
          handleSearch(powerSearchUserInput?.searchQuery?.trim());
        }
      }
    }
  }, [toggleSearchEnabled, toggleSearchSwitch]);


  useEffect(() => {
    if (userPreferenceData) {
      if (userPreferenceData?.quizMode) {
        setToggleSearchSwitch(userPreferenceData.quizMode === 'POWER_SEARCH' ? true : false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferenceData]);

  useEffect(() => {
    onPowerSearchModeChange(toggleSearchSwitch);
  }, [toggleSearchSwitch]);

  useEffect(() => {
    if (toggleSearchEnabled, toggleSearchSwitch) {
      if (searchResults?.topics?.length > 0 || searchResults?.subtopics?.length > 0) {
        if (powerSearchUserInput?.selectedTopics?.length > 0) {
          setTimeout(() => {
            onTopicsCatSelect(powerSearchUserInput.selectedTopics);
          }, 1000);
        }
        if (powerSearchUserInput?.selectedSubTopics?.length > 0) {
          setTimeout(() => {
            onSubTopicsCatSelect(powerSearchUserInput.selectedSubTopics);
          }, 1000);
        }
      }
    }

  }, [searchResults]);

  const latestValues = useRef({ searchQuery, selectedTopics, selectedSubTopics });

  useEffect(() => {
    latestValues.current = { searchQuery, selectedTopics, selectedSubTopics };
  }, [searchQuery, selectedTopics, selectedSubTopics]);

  useEffect(() => {
    return () => {
      // set the powerSearchUserInput to the latestValues
      dispatch(setPowerSearchUserInput({
        searchQuery: latestValues.current.searchQuery,
        selectedTopics: latestValues.current.selectedTopics,
        selectedSubTopics: latestValues.current.selectedSubTopics
      }));
    };
  }, []);

  useEffect(() => {
    if (onSearchResults?.loading !== undefined && onSearchResults?.topicsSearchResults) {
      setSearchResults(onSearchResults.topicsSearchResults);
    }
  }, [onSearchResults]);

  useEffect(() => {
    if (toggleSearchEnabled && toggleSearchSwitch && searchQuery && isSuggestionSelected) {
      const { topics = [], subtopics = [] } = searchResults;

      const matchedTopic = topics.find(topic =>
        topic.toLowerCase() === searchQuery?.trim()?.toLowerCase()
      );

      // Find exact matches in subtopics
      const matchedSubTopic = subtopics.find(subtopic =>
        subtopic.toLowerCase() === searchQuery?.trim()?.toLowerCase()
      );

      if (matchedTopic) {
        onTopicsCatSelect([...selectedTopics, matchedTopic]);
      }

      if (matchedSubTopic) {
        onSubTopicsCatSelect([...selectedSubTopics, matchedSubTopic]);
      }
    }
  }, [searchQuery, searchResults, toggleSearchEnabled, toggleSearchSwitch, isSuggestionSelected]);

  const handleAccordionToggle = (panel) => (event, newExpanded) => {
    if (event.target.nodeName.toLowerCase() !== "input") {
      event.stopPropagation();
      setExpanded(newExpanded ? panel : false);
    }
  };

  const selectCategory = (evt, item, type) => {
    evt.stopPropagation();
    const isChecked = evt.target.checked;

    if (categoryMap[type]) {
      const { selected, handler } = categoryMap[type];
      const targetCategory = [...selected];

      if (isChecked) {
        targetCategory.push(item);
      } else {
        const index = targetCategory.indexOf(item);
        if (index !== -1) {
          targetCategory.splice(index, 1);
        }
      }

      handler(targetCategory);
    }
  };

  const selectAll = (evt, type) => {
    evt.stopPropagation();
    const isChecked = evt.target.checked;
    const { handler, total } = categoryMap[type];
    handler(isChecked ? total : []);
  };

  const selectAllTopicsSubtopicsResults = (evt, type) => {
    evt.stopPropagation();
    const isChecked = evt.target.checked;
    const currentResults = type === 'topics' ? searchResults.topics : searchResults.subtopics;
    const handler = type === 'topics' ? onTopicsCatSelect : onSubTopicsCatSelect;
    const currentSelected = type === 'topics' ? selectedTopics : selectedSubTopics;

    if (isChecked) {
      // Add all current search results to existing selection
      handler([...currentSelected, ...currentResults]);
    } else {
      // Remove only items from current search results
      const remainingItems = currentSelected.filter(item => !currentResults.includes(item));
      handler(remainingItems);
    }
  };

  const setChecked = (item, type) =>
    categoryMap[type]?.selected?.includes(item) || false;

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    // setSearchQuery(value);
  };

  const debouncedFetch = useCallback(
    debounce((value) => {
      if (value?.length >= CONSTANTS.powerSearchCharLength) {
        handleSearch(value);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (searchQuery?.length >= CONSTANTS.powerSearchCharLength) {
      debouncedFetch(searchQuery);
    } else {
      dispatch(resetSearchTopics());
    }
  }, [searchQuery, debouncedFetch, dispatch]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim().length >= CONSTANTS.powerSearchCharLength) {
      e.preventDefault();
      handleSearch();
    }
  };


  const handleSearch = (userInput = '') => {
    const searchVal = typeof userInput === 'string' && userInput || searchQuery.trim()
    if (searchVal.trim() !== powerSearchUserInput?.searchQuery?.trim()) {
      dispatch(resetPowerSearchUserInput());
    }
    dispatch(searchTopics(searchVal));
  };

  const handleToggleSwitch = () => {
    const isPowerSearch = toggleSearchSwitch;
    // onPowerSearchModeChange(!isPowerSearch);
    setToggleSearchSwitch(!isPowerSearch);
  };

  const handleSuggestionSelect = (value) => {
    setIsSuggestionSelected(true);
    handleSearch(value);
  };

  const renderCheckboxItem = (item, type, index) => (
    <AccordionDetails key={index} data-testid={`category-item-${type}-${index}`}>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(evt) => selectCategory(evt, item, type)}
              checked={setChecked(item, type)}
              name={item}
              disabled={isViewDisabled}
              color="primary"
              data-testid={`checkbox-${type}-${item}`}
            />
          }
          label={item}
          data-testid={`label-${type}-${item}`}
        />
      </FormGroup>
    </AccordionDetails>
  );

  const renderAccordion = (type, title) => {
    const { selected, total } = categoryMap[type];
    const sortedItems = total?.sort((a, b) => a.localeCompare(b)) || [];

    return (
      <Accordion
        className={
          hideCategoryWhenEmpty && !total?.length
            ? "hide-category-accordion"
            : ""
        }
        square
        expanded={expanded === type}
        onChange={handleAccordionToggle(type)}
        data-testid={`accordion-${type}`}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore />} 
          className="categories-summary"
          data-testid={`accordion-summary-${type}`}
        >
          <Checkbox
            onChange={(evt) => selectAll(evt, type)}
            checked={total?.length > 0 && total?.length === selected?.length}
            name={type}
            disabled={isViewDisabled}
            color="primary"
            data-testid={`select-all-${type}`}
          />
          <Typography className="cat-heading" data-testid={`category-title-${type}`}>
            {title}
          </Typography>
        </AccordionSummary>
        {sortedItems.map((item, index) => renderCheckboxItem(item, type, index))}
      </Accordion>
    );
  };

  const renderSearchResults = () => {
    const { topics = [], subtopics = [] } = searchResults;

    if (isSearchLoading) {
      return (
        <div className="search-loading" data-testid="search-loading">
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 10 }}>
            Searching topics...
          </Typography>
        </div>
      );
    }

    if (searchError) {
      return (
        <div className="search-error" data-testid="search-error">
          <Typography variant="body2" className="search-error-message" data-testid="search-error-message">
            Search failed. Please try again.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            className="search-retry-button"
            onClick={() => handleSearch(searchQuery.trim())}
            disabled={searchQuery.trim().length < CONSTANTS.powerSearchCharLength}
            data-testid="search-retry-button"
          >
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="search-results" data-testid="search-results">
        {topics?.length === 0 && subtopics?.length === 0 && (
          <Typography variant="body2" className="no-results-message" data-testid="no-results-message">
            {searchQuery ? "No results found" : "Enter a search term to find categories"}
          </Typography>
        )}
        <>
          <Box className="topic-header" data-testid="topics-header">
            <FormControlLabel
              control={
                topics?.length > 0 && searchQuery.length >= CONSTANTS.powerSearchCharLength ? (
                  <Checkbox
                    onChange={(evt) => selectAllTopicsSubtopicsResults(evt, 'topics')}
                    checked={topics?.length > 0 &&
                      topics?.every(topic => selectedTopics?.includes(topic))}
                    color="primary"
                    data-testid="select-all-topics"
                  />
                ) : <></>
              }
              label="Topics:"
              data-testid="topics-label"
            />
          </Box>
          {topics?.length > 0 && searchQuery.length >= CONSTANTS.powerSearchCharLength ? (
            <Box className="topics-cont" data-testid="topics-container">
              {topics.map((result, index) => (
                <FormGroup key={index} data-testid={`topic-item-${index}`}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(evt) => selectCategory(evt, result, "topics")}
                        checked={setChecked(result, "topics")}
                        name={result}
                        color="primary"
                        data-testid={`checkbox-topics-${result}`}
                      />
                    }
                    label={result}
                    data-testid={`label-topics-${result}`}
                  />
                </FormGroup>
              ))}
            </Box>) : (
            <>
              <Typography variant="body2" className="no-results-message" data-testid="no-topics-message">
                No Topics to show
              </Typography>
            </>
          )}

        </>


        <>
          <Box className="topic-header" data-testid="subtopics-header">
            <FormControlLabel
              control={
                subtopics?.length > 0 && searchQuery.length >= CONSTANTS.powerSearchCharLength ? (
                  <Checkbox
                    onChange={(evt) => selectAllTopicsSubtopicsResults(evt, 'subtopics')}
                    checked={subtopics?.length > 0 && 
                      subtopics?.every(subtopic => selectedSubTopics?.includes(subtopic))}
                    color="primary"
                    data-testid="select-all-subtopics"
                  />
                ) : <></>

              }
              label="Sub-Topics:"
              data-testid="subtopics-label"
            />
          </Box> {subtopics?.length > 0 && searchQuery.length >= CONSTANTS.powerSearchCharLength ? (
            <Box className="subtopics-cont" data-testid="subtopics-container">
              {subtopics.map((result, index) => (
                <FormGroup key={index} data-testid={`subtopic-item-${index}`}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(evt) => selectCategory(evt, result, "subtopics")}
                        checked={setChecked(result, "subtopics")}
                        name={result}
                        color="primary"
                        data-testid={`checkbox-subtopics-${result}`}
                      />
                    }
                    label={result}
                    data-testid={`label-subtopics-${result}`}
                  />
                </FormGroup>
              ))}
            </Box>
          ) : (
            <>
              <Typography variant="body2" className="no-results-message" data-testid="no-subtopics-message">
                No Sub-Topics to show
              </Typography>
            </>
          )}
        </>

      </div>
    );
  };

  useEffect(() => {
    setToggleSearchEnabled(CONSTANTS.powerSearch);
    return () => {
      // dispatch(resetSearchTopics());
    };
  }, [dispatch]);

  return (
    <div className="categories-container" data-testid="categories-container">
      {toggleSearchEnabled && !hidePowerSearch && (
        <Box
          className="toggle-switch-container"
          sx={{ display: "flex", justifyContent: "flex-end" }}
          data-testid="power-search-toggle-container"
        >
          <FormControlLabel
            control={
              <Switch
                checked={toggleSearchSwitch}
                onChange={handleToggleSwitch}
                color="primary"
                data-testid="power-search-switch"
              />
            }
            // Static label: the switch itself communicates on/off (and screen
            // readers announce its checked state) — the dynamic "On/Off" suffix
            // made the row wider than the 768px rail and clipped the switch
            // (beauty-pack after-verify).
            label="Power Search"
            data-testid="power-search-label"
          />
        </Box>
      )}
      {(toggleSearchSwitch && !hidePowerSearch )? (
        <>
          {
            !hidePowerSearch && (
              <div className="search-container" data-testid="search-container">
                <Typography variant="h6" gutterBottom data-testid="power-search-title">
                  Power Search
                </Typography>
                <div className="search-input-box" data-testid="search-input-container">
                  {/* <PowerSearchField
                autocomplete={CONSTANTS.powerSearchAutoComplete}
                defaultQuery={searchQuery}
                handleSearchInputChange={handleSearchInputChange}
                handleKeyPress={handleKeyPress}
                handleSearch={handleSearch}
                handleSuggestionSelect={handleSuggestionSelect}
              /> */}
                  <TextField
                    label="Search topics"
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Type keywords to search..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyPress={handleKeyPress}
                    inputProps={{ maxLength: 30, enterKeyHint: 'search' }}
                    data-testid="search-input"
                    helperText={
                      searchQuery.length < CONSTANTS.powerSearchCharLength && searchQuery.length > 0
                        ? `Minimum ${CONSTANTS.powerSearchCharLength} characters are required`
                        : ""
                    }
                  />
                  
                  {!CONSTANTS.powerSearchAutoComplete && (
                    <Button
                      variant="contained"
                      color="primary"
                      className="search-button"
                      onClick={handleSearch}
                      disabled={searchQuery.trim().length < CONSTANTS.powerSearchCharLength || isSearchLoading}
                      data-testid="search-button"
                    >
                      {isSearchLoading ? "Searching..." : "Search"}
                    </Button>
                  )}

                </div>
                
                {renderSearchResults()}
                <Box sx={{mt: 2}}>
                  {renderAccordion("examType", "Exam Type")}
                </Box>
               
              </div>
            )
          }
        </>
      ) : (
      <section className="categories" data-testid="categories-section">
        {renderAccordion("examType", "Exam Type")}
        {renderAccordion("organSystem", "Organ System")}
        {renderAccordion("discipline", "Discipline")}
      </section>
      )}
    </div>
  );
});

export default Categories;
